import { getApiUrl } from '@/config/env';

const API_BASE_URL = getApiUrl();
// Module loaded - debug log removed in production

export interface UploadResult {
  filename: string;
  uploadedFileName: string;
  assetId: string;
  success: boolean;
  error?: string;
}

// Unified upload function that handles both single and multiple files
export const uploadFilesAction = async (
  files: File[],
  storeId: string,
  path: string = "/store/products/images",
  onProgress?: (
    progress: {
      filename: string;
      status: "uploading" | "completed" | "error";
      progress: number;
    }[],
  ) => void,
): Promise<UploadResult[]> => {
  if (!files || files.length === 0) {
    console.warn("⚠️ No files provided to upload");
    return [];
  }

  const uploadPromises = files.map(
    async (file, _index): Promise<UploadResult> => {
      try {
        // Update progress - starting upload
        onProgress?.([
          { filename: file.name, status: "uploading", progress: 0 },
        ]);

        // Step 1: Get upload URL with filename in query params
        const url = new URL(
          `${API_BASE_URL}/api/v1/admin/${storeId}/assets/upload`,
        );
        url.searchParams.append("path", path);
        url.searchParams.append("filename", file.name);

        const res = await fetch(url.toString(), {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken") || ""}`,
          },
        });

        if (!res.ok) {
          const errorText = await res.text();
          console.error(
            `❌ Failed to get upload URL for ${file.name}:`,
            errorText,
          );
          throw new Error(
            `Failed to get upload URL: ${res.status} ${res.statusText}`,
          );
        }

        const data = await res.json();

        if (!data.valid || !data.data || !data.data.upload_url) {
          console.error(`❌ Invalid response for ${file.name}:`, data);
          throw new Error("Invalid response from upload URL endpoint");
        }

        // Update progress - got URL
        onProgress?.([
          { filename: file.name, status: "uploading", progress: 25 },
        ]);

        // Step 2: Upload file to the pre-signed URL
        const uploadResponse = await fetch(data.data.upload_url, {
          method: "PUT",
          body: await file.arrayBuffer(),
          headers: {
            "Content-Type": file.type,
          },
        });

        if (!uploadResponse.ok) {
          const errorText = await uploadResponse.text();
          console.error(`❌ Failed to upload file ${file.name}:`, errorText);
          throw new Error(
            `Failed to upload file: ${uploadResponse.status} ${uploadResponse.statusText}`,
          );
        }

        onProgress?.([
          { filename: file.name, status: "completed", progress: 100 },
        ]);

        return {
          filename: file.name,
          uploadedFileName: data.data.uploaded_file_name || file.name,
          assetId: data.data.asset_id || "",
          success: true,
        };
      } catch (error) {
        onProgress?.([{ filename: file.name, status: "error", progress: 0 }]);

        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";
        console.error(`❌ Upload failed for ${file.name}:`, errorMessage);

        return {
          filename: file.name,
          uploadedFileName: "",
          assetId: "",
          success: false,
          error: errorMessage,
        };
      }
    },
  );

  // Execute all uploads in parallel
  const results = await Promise.all(uploadPromises);

  return results;
};

// Legacy function for backward compatibility - now uses the unified function
// Returns asset ID instead of filename
export const uploadImageAction = async (
  file: File,
  storeId: string,
): Promise<string> => {
  try {
    const results = await uploadFilesAction([file], storeId);
    const result = results[0];

    if (result.success) {
      return result.assetId;
    } else {
      console.error("Legacy upload failed:", result.error);
      return "";
    }
  } catch (error) {
    console.error("uploadImageAction: Caught error:", error);
    return "";
  }
};

// Convenience function for single file upload
export const uploadSingleFileAction = async (
  file: File,
  storeId: string,
  path: string = "/store/products/images",
): Promise<UploadResult> => {
  const results = await uploadFilesAction([file], storeId, path);
  return results[0];
};

// Convenience function for multiple files (same as main function but with clearer name)
export const uploadMultipleFilesAction = uploadFilesAction;
